import type { QuizQuestion, Topic } from "./types";

export type IRTParams = {
  difficulty: number;
  discrimination: number;
  guessing: number;
};

export type CalibratedQuestion = QuizQuestion & {
  irtParams: IRTParams;
  calibratedForTopicId: string;
  timesAsked: number;
  timesCorrect: number;
  lastAsked: string | null;
};

export type AssessmentItem = {
  question: CalibratedQuestion;
  estimatedAbility: number;
  informationValue: number;
  selected: boolean;
  reason: string;
};

export type MicroAssessment = {
  id: string;
  targetTopicId: string;
  targetTopicName: string;
  items: AssessmentItem[];
  currentEstimate: AbilityEstimate;
  standardError: number;
  isComplete: boolean;
  recommendation: string;
};

export type AbilityEstimate = {
  theta: number;
  standardError: number;
  confidence: "low" | "medium" | "high";
};

const DEFAULT_IRT_PARAMS: IRTParams = {
  difficulty: 0,
  discrimination: 1,
  guessing: 0.25
};

export function calibrateQuestion(
  question: QuizQuestion,
  topicId: string
): CalibratedQuestion {
  const difficulty = estimateDifficultyFromTopic(question, topicId);
  
  return {
    ...question,
    irtParams: {
      difficulty,
      discrimination: DEFAULT_IRT_PARAMS.discrimination,
      guessing: DEFAULT_IRT_PARAMS.guessing
    },
    calibratedForTopicId: topicId,
    timesAsked: 0,
    timesCorrect: 0,
    lastAsked: null
  };
}

function estimateDifficultyFromTopic(
  question: QuizQuestion,
  topicId: string
): number {
  let baseDifficulty = 0;
  
  if (question.prerequisiteTopicIds?.length) {
    baseDifficulty = Math.min(1, question.prerequisiteTopicIds.length * 0.2);
  }
  
  if (question.prompt.length > 100) {
    baseDifficulty += 0.1;
  }
  
  if (question.choices.length > 4) {
    baseDifficulty += 0.1;
  }
  
  if (question.conceptTag?.toLowerCase().includes("advanced")) {
    baseDifficulty += 0.3;
  }
  
  return Math.max(-2, Math.min(2, baseDifficulty));
}

export function calculateProbability(theta: number, params: IRTParams): number {
  const { difficulty, discrimination, guessing } = params;
  
  const exponent = -discrimination * (theta - difficulty);
  const probability = guessing + (1 - guessing) / (1 + Math.exp(exponent));
  
  return Math.max(0.01, Math.min(0.99, probability));
}

export function calculateInformation(
  theta: number,
  params: IRTParams
): number {
  const p = calculateProbability(theta, params);
  const { discrimination, guessing } = params;
  
  const q = 1 - p;
  const pStar = (p - guessing) / (1 - guessing);
  const qStar = 1 - pStar;
  
  if (p <= guessing || p >= 1 || q <= 0) return 0;
  
  const info = (discrimination * discrimination * q * pStar * pStar) / (p * qStar);
  
  return Math.max(0, info);
}

export function estimateAbilityFromResponses(
  responses: Array<{ correct: boolean; question: CalibratedQuestion }>,
  priorEstimate: number = 0
): AbilityEstimate {
  if (responses.length === 0) {
    return {
      theta: priorEstimate,
      standardError: 1.0,
      confidence: "low"
    };
  }
  
  let theta = priorEstimate;
  const learningRate = 0.5;
  
  for (let iteration = 0; iteration < 20; iteration++) {
    let numerator = 0;
    let denominator = 0;
    
    for (const { correct, question } of responses) {
      const p = calculateProbability(theta, question.irtParams);
      const info = calculateInformation(theta, question.irtParams);
      
      if (info > 0) {
        if (correct) {
          numerator += (1 - p) * info;
        } else {
          numerator -= p * info;
        }
        denominator += info;
      }
    }
    
    if (denominator === 0) break;
    
    const delta = numerator / denominator * learningRate;
    theta = Math.max(-3, Math.min(3, theta + delta));
    
    if (Math.abs(delta) < 0.001) break;
  }
  
  let totalInfo = 0;
  for (const { question } of responses) {
    totalInfo += calculateInformation(theta, question.irtParams);
  }
  
  const standardError = totalInfo > 0 ? 1 / Math.sqrt(totalInfo) : 1.0;
  
  let confidence: "low" | "medium" | "high";
  if (standardError > 0.5) {
    confidence = "low";
  } else if (standardError > 0.3) {
    confidence = "medium";
  } else {
    confidence = "high";
  }
  
  return { theta, standardError, confidence };
}

export function selectNextQuestion(
  questions: CalibratedQuestion[],
  currentEstimate: AbilityEstimate,
  alreadyAsked: Set<string>
): CalibratedQuestion | null {
  const available = questions.filter(q => !alreadyAsked.has(q.id));
  
  if (available.length === 0) return null;
  
  const scored = available.map(q => {
    const info = calculateInformation(currentEstimate.theta, q.irtParams);
    const timeSinceAsked = q.lastAsked
      ? (Date.now() - new Date(q.lastAsked).getTime()) / (1000 * 60 * 60 * 24)
      : 30;
    const recencyBonus = Math.min(0.2, timeSinceAsked * 0.01);
    const adjustedInfo = info * (1 + recencyBonus);
    
    return { question: q, score: adjustedInfo };
  });
  
  scored.sort((a, b) => b.score - a.score);
  
  const topQuestions = scored.slice(0, Math.min(3, scored.length));
  const selected = topQuestions[Math.floor(Math.random() * topQuestions.length)];
  
  return selected?.question || null;
}

export function createMicroAssessment(
  targetTopic: Topic,
  questionBank: CalibratedQuestion[],
  maxQuestions: number = 3
): MicroAssessment {
  const topicQuestions = questionBank.filter(
    q => q.calibratedForTopicId === targetTopic.id
  );
  
  const prerequisiteQuestions = targetTopic.prerequisiteTopicIds
    ?.flatMap(prereqId =>
      questionBank.filter(q => q.calibratedForTopicId === prereqId)
    ) || [];
  
  const relevantQuestions = [...topicQuestions, ...prerequisiteQuestions];
  
  if (relevantQuestions.length === 0) {
    return {
      id: `micro-${targetTopic.id}-${Date.now()}`,
      targetTopicId: targetTopic.id,
      targetTopicName: targetTopic.name,
      items: [],
      currentEstimate: { theta: 0, standardError: 1, confidence: "low" },
      standardError: 1,
      isComplete: true,
      recommendation: "No assessment questions available for this topic yet."
    };
  }
  
  return {
    id: `micro-${targetTopic.id}-${Date.now()}`,
    targetTopicId: targetTopic.id,
    targetTopicName: targetTopic.name,
    items: [],
    currentEstimate: { theta: 0, standardError: 1, confidence: "low" },
    standardError: 1,
    isComplete: false,
    recommendation: ""
  };
}

export function addQuestionToAssessment(
  assessment: MicroAssessment,
  question: CalibratedQuestion
): AssessmentItem {
  const info = calculateInformation(assessment.currentEstimate.theta, question.irtParams);
  
  return {
    question,
    estimatedAbility: assessment.currentEstimate.theta,
    informationValue: info,
    selected: true,
    reason: info > 0.5
      ? "High information question at current ability level"
      : info > 0.2
      ? "Moderate information question"
      : "Lower information but needed for coverage"
  };
}

export function updateAssessmentEstimate(
  assessment: MicroAssessment,
  responses: Array<{ questionId: string; correct: boolean }>
): {
  updatedAssessment: MicroAssessment;
  isComplete: boolean;
  estimatedAbility: AbilityEstimate;
} {
  const answeredItems = assessment.items.filter(item =>
    responses.some(r => r.questionId === item.question.id)
  );
  
  const bktResponses = answeredItems.map(item => {
    const response = responses.find(r => r.questionId === item.question.id)!;
    return { correct: response.correct, question: item.question };
  });
  
  const newEstimate = estimateAbilityFromResponses(
    bktResponses,
    assessment.currentEstimate.theta
  );
  
  const updatedAssessment: MicroAssessment = {
    ...assessment,
    currentEstimate: newEstimate,
    standardError: newEstimate.standardError,
    isComplete: newEstimate.confidence === "high" || answeredItems.length >= 5,
    recommendation: generateRecommendation(newEstimate, assessment.targetTopicName)
  };
  
  return {
    updatedAssessment,
    isComplete: updatedAssessment.isComplete,
    estimatedAbility: newEstimate
  };
}

function generateRecommendation(estimate: AbilityEstimate, topicName: string): string {
  const percent = Math.round(((estimate.theta + 3) / 6) * 100);
  
  if (estimate.confidence === "high") {
    if (estimate.theta > 0.5) {
      return `Strong understanding of ${topicName}. You're ready to move to more advanced topics.`;
    } else if (estimate.theta > -0.5) {
      return `Good understanding of ${topicName}. A bit more practice will solidify your grasp.`;
    } else {
      return `Understanding of ${topicName} needs development. Review the foundational concepts.`;
    }
  }
  
  return `Need more data to confirm understanding of ${topicName}. Continue with a few more questions.`;
}

export function convertMasteryToTheta(masteryPercent: number): number {
  return ((masteryPercent / 100) * 6) - 3;
}

export function convertThetaToMastery(theta: number): number {
  return Math.round(((theta + 3) / 6) * 100);
}

export function measureGapSeverity(
  expectedMastery: number,
  actualMastery: number
): "low" | "medium" | "high" {
  const gap = expectedMastery - actualMastery;
  
  if (gap < 10) return "low";
  if (gap < 25) return "medium";
  return "high";
}

export function selectQuestionsForValidation(
  gapTopicId: string,
  questionBank: CalibratedQuestion[],
  count: number = 2
): CalibratedQuestion[] {
  const questions = questionBank
    .filter(q => q.calibratedForTopicId === gapTopicId)
    .sort((a, b) => {
      const aRecent = a.lastAsked ? new Date(a.lastAsked).getTime() : 0;
      const bRecent = b.lastAsked ? new Date(b.lastAsked).getTime() : 0;
      return aRecent - bRecent;
    });
  
  return questions.slice(0, count);
}

export function updateQuestionCalibration(
  question: CalibratedQuestion,
  correct: boolean
): CalibratedQuestion {
  const newTimesAsked = question.timesAsked + 1;
  const newTimesCorrect = question.timesCorrect + (correct ? 1 : 0);
  
  const observedP = newTimesCorrect / newTimesAsked;
  
  const newDifficulty = estimateDifficultyFromResponse(question, observedP, newTimesAsked);
  
  const newDiscrimination = Math.max(0.3, Math.min(2.5,
    question.irtParams.discrimination * (1 + (newTimesAsked > 10 ? 0.01 : 0))
  ));
  
  return {
    ...question,
    timesAsked: newTimesAsked,
    timesCorrect: newTimesCorrect,
    lastAsked: new Date().toISOString(),
    irtParams: {
      ...question.irtParams,
      difficulty: newDifficulty,
      discrimination: newDiscrimination
    }
  };
}

function estimateDifficultyFromResponse(
  question: CalibratedQuestion,
  observedP: number,
  timesAsked: number
): number {
  if (timesAsked < 3) return question.irtParams.difficulty;
  
  const weight = Math.min(0.3, timesAsked / 100);
  const boundedObservedP = Math.max(0.01, Math.min(0.99, observedP));
  const observedDifficulty = -Math.log((1 / boundedObservedP - 1)) / question.irtParams.discrimination;
  
  return question.irtParams.difficulty * (1 - weight) + observedDifficulty * weight;
}

export function getOptimalQuestionCount(
  currentSE: number,
  targetSE: number = 0.3
): number {
  if (currentSE <= targetSE) return 0;
  
  const seRatio = targetSE / currentSE;
  const estimatedQuestions = Math.ceil(1 / (seRatio * seRatio) - 1);
  
  return Math.max(1, Math.min(10, estimatedQuestions));
}
